import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "../../lib/auth";
import { isDealerUser } from "../../lib/roles";
import Seo from "../../components/Seo";

export default function DealerLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(identifier.trim(), password);
      if (!isDealerUser(u)) {
        setErr("This portal is for dealer team members only.");
        return;
      }
      // Honor ?next= from RequireAdmin redirects; otherwise default to /admin/today.
      const next = searchParams.get("next");
      const safeNext = next && next.startsWith("/admin") ? next : "/admin/today";
      nav(safeNext, { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="bg-[var(--ssc-navy)] min-h-[80vh] py-16">
      <Seo title="Dealer Sign-In" description="" />
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Dealer Portal</div>
          <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Team Sign-In</h1>
          <p className="text-[var(--ssc-ink-2)] mt-1 text-sm">Use your username or email.</p>

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="dealer-login-form">
            <div>
              <label className="ssc-label">Username or Email</label>
              <input data-testid="dealer-login-email" className="ssc-input" type="text" value={identifier}
                     onChange={(e) => setIdentifier(e.target.value)} required autoComplete="username"
                     placeholder="JeremyF or jeremyf@scottsellschevys.com" />
            </div>
            <div>
              <label className="ssc-label">Password</label>
              <div className="relative">
                <input data-testid="dealer-login-password" className="ssc-input pr-10"
                       type={showPw ? "text" : "password"} value={password}
                       onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7c93] hover:text-[var(--ssc-navy)] p-1.5"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        data-testid="dealer-login-show-password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {err && <div className="text-sm text-[var(--ssc-red)]" data-testid="dealer-login-error">{err}</div>}
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full" data-testid="dealer-login-submit">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
